
import { db, serverTimestamp } from "../../firebaseClient";
import type { NotificationDoc } from "../types/legalOps";

const COLLECTION = "notifications";
const MAIL_QUEUE = "mail_queue";

export async function fetchUnreadNotifications(limitCount = 20): Promise<NotificationDoc[]> {
  try {
    const snapshot = await db.collection(COLLECTION)
      .where("read", "==", false)
      .orderBy("createdAt", "desc")
      .limit(limitCount)
      .get();
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as NotificationDoc));
  } catch (e) {
    console.error("Error fetching unread notifications", e);
    return [];
  }
}

export async function fetchAllNotifications(limitCount = 100): Promise<NotificationDoc[]> {
  try {
    const snapshot = await db.collection(COLLECTION)
      .orderBy("createdAt", "desc")
      .limit(limitCount)
      .get();
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as NotificationDoc));
  } catch (e) {
    console.error("Error fetching all notifications", e);
    return [];
  }
}

export async function markAllAsRead(): Promise<void> {
  try {
    const snapshot = await db.collection(COLLECTION).where("read", "==", false).get();
    if (snapshot.empty) return;
    
    const batch = db.batch();
    snapshot.docs.forEach(doc => {
      batch.update(doc.ref, { read: true });
    });
    await batch.commit();
  } catch (e) {
    console.error("Error marking all as read", e);
    throw e;
  }
}

export type ActionNotificationParams = {
  docType: "contract" | "seal" | "coreDoc" | "govProject" | "govLetter" | "contractReview" | "contractDraft" | "sealRequest" | "other";
  docId: string;
  docTitle: string;
  assigneeId: string;
  assigneeEmail: string; // Required for emailing
  actionType: "REVIEW" | "APPROVE" | "RETURNED" | "FYI";
  note?: string;
};

/**
 * Creates an in-app notification AND queues an email via Firestore `mail_queue`
 */
export async function notifyActionRequired(params: ActionNotificationParams) {
  const { docType, docId, docTitle, assigneeId, assigneeEmail, actionType, note } = params;
  
  if (!assigneeEmail) {
      console.warn("[Notification] Missing assigneeEmail, skipping email queue.");
      return;
  }

  try {
    // 1. In-App Notification
    const notifPayload = {
      category: docType,
      sourceType: docType,
      sourceId: docId,
      title: `待辦事項：${docTitle}`,
      detail: `請前往處理 (${actionType})。${note ? `備註：${note}` : ''}`,
      date: new Date().toISOString().split('T')[0],
      eventDate: new Date().toISOString().split('T')[0],
      read: false,
      severity: "upcoming",
      createdAt: serverTimestamp()
    };
    
    // In-app notification writing
    await db.collection(COLLECTION).add(notifPayload);

    // 2. Email Queue (Cloud Function Trigger: onMailQueueCreate)
    const emailSubject = `SF法務系統通知：有一件【${docTitle}】待辦事項等您處理`;
    
    const actionMap: Record<string, string> = {
        'REVIEW': '審查',
        'APPROVE': '核准',
        'RETURNED': '退回修正',
        'FYI': '查看'
    };
    const actionLabel = actionMap[actionType] || actionType;

    const emailBody = `
      <div style="font-family: 'Helvetica Neue', Arial, sans-serif; color: #333; max-width: 600px; padding: 20px; border: 1px solid #e5e7eb; border-radius: 8px;">
        <h3 style="color: #2563eb; border-bottom: 2px solid #e5e7eb; padding-bottom: 10px;">待辦事項通知</h3>
        <p>您好，系統中有一件文件需要您的處理：</p>
        <ul style="background: #f9fafb; padding: 15px 30px; border-radius: 6px; list-style-type: circle;">
          <li style="margin-bottom: 8px;"><strong>類型：</strong> ${docType}</li>
          <li style="margin-bottom: 8px;"><strong>標題：</strong> ${docTitle}</li>
          <li style="margin-bottom: 8px;"><strong>動作：</strong> <span style="color:#d97706; font-weight:bold;">${actionLabel}</span></li>
          ${note ? `<li><strong>備註：</strong> ${note}</li>` : ''}
        </ul>
        <p>請盡速登入系統辦理。</p>
        <div style="margin-top: 25px; text-align: center;">
            <a href="https://legalops.app" style="background-color: #2563eb; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px; font-weight: bold;">前往 SF 法務系統</a>
        </div>
      </div>
    `;

    // Write to mail_queue for backend processing (onMailQueueCreate)
    await db.collection(MAIL_QUEUE).add({
      to: [assigneeEmail], // Must be array
      subject: emailSubject,
      html: emailBody,
      text: `請登入系統處理待辦事項：${docTitle}`, // Fallback
      status: 'PENDING', // Initial Status
      attempts: 0,
      createdAt: serverTimestamp(),
      meta: {
        docId,
        docType,
        assigneeId
      }
    });

    console.log(`[Notification] Action queued for ${assigneeEmail}: ${docTitle} (${actionType})`);

  } catch (e) {
    console.error("[Notification] Failed to notify", e);
  }
}
